import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { CheckCheck, Search, X } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import {
  ENTITY_FINDER_INPUT_ANIMATION_MS,
  ENTITY_FINDER_OPEN_DELAY_MS,
  ENTITY_FINDER_PANEL_FADE_MS,
  ENTITY_FINDER_POPOVER_CLOSE_MS,
} from "@/modern/components/entity-finder/constants/layout";

import {
  SURFACE_BACK_BTN_CLS,
  SURFACE_ICON_BTN_CLS,
  SURFACE_ICON_GLYPH_CLS,
  SURFACE_SEARCH_CLS,
} from "../constants/layout";

/** Same open/close easing as SwivuEntityFinder (global header search). */
const PANEL_TRANSITION = {
  duration: ENTITY_FINDER_PANEL_FADE_MS / 1000,
  ease: "easeOut" as const,
};
const INPUT_REVEAL_TRANSITION = {
  duration: ENTITY_FINDER_INPUT_ANIMATION_MS / 1000,
  ease: [0.22, 1, 0.36, 1] as const,
};

/** Expanded width — Updates list filter, narrower than the global entity finder. */
const EXPANDED_WIDTH_CLS = "w-[min(20rem,36vw)]";

const MARK_ALL_DISABLED_REASON = "Nothing left to mark as seen";

/**
 * Header chrome: "Mark all as seen" then search. Search starts as an icon; click
 * grows it in-flow to the field width (title truncates) with the same timings as
 * global header search.
 */
export function NotificationHeaderListChrome({
  value,
  onChange,
  placeholder = "Filter updates…",
  onMarkAllSeen,
  canMarkAllSeen,
  isMarkingAll = false,
  showMarkAll = true,
  afterMarkAll,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onMarkAllSeen: () => void;
  /** False when the current list has nothing left to mark seen. */
  canMarkAllSeen: boolean;
  isMarkingAll?: boolean;
  showMarkAll?: boolean;
  afterMarkAll?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const isExpandedRef = useRef(value.trim().length > 0);
  const isClosingRef = useRef(false);
  const closeTimeoutRef = useRef<number>();
  const openDelayRef = useRef<number>();
  const expandRafRef = useRef<number | null>(null);

  const [isExpanded, setIsExpanded] = useState(() => value.trim().length > 0);
  const [inputRevealed, setInputRevealed] = useState(() => value.trim().length > 0);

  useEffect(() => {
    isExpandedRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current != null) window.clearTimeout(closeTimeoutRef.current);
      if (openDelayRef.current != null) window.clearTimeout(openDelayRef.current);
      if (expandRafRef.current != null) cancelAnimationFrame(expandRafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isExpanded) {
      setInputRevealed(false);
      isClosingRef.current = false;
      if (openDelayRef.current != null) window.clearTimeout(openDelayRef.current);
      return;
    }

    openDelayRef.current = window.setTimeout(() => {
      openDelayRef.current = undefined;
      setInputRevealed(true);
    }, ENTITY_FINDER_OPEN_DELAY_MS);

    return () => {
      if (openDelayRef.current != null) window.clearTimeout(openDelayRef.current);
    };
  }, [isExpanded]);

  const focusInput = useCallback(() => {
    if (expandRafRef.current != null) cancelAnimationFrame(expandRafRef.current);
    expandRafRef.current = requestAnimationFrame(() => {
      expandRafRef.current = requestAnimationFrame(() => {
        expandRafRef.current = null;
        if (!isExpandedRef.current) return;
        inputRef.current?.focus();
      });
    });
  }, []);

  const expandAndFocus = useCallback(() => {
    if (isClosingRef.current) return;
    isExpandedRef.current = true;
    setIsExpanded(true);
    focusInput();
  }, [focusInput]);

  const collapse = useCallback(() => {
    if (expandRafRef.current != null) {
      cancelAnimationFrame(expandRafRef.current);
      expandRafRef.current = null;
    }
    isExpandedRef.current = false;
    setIsExpanded(false);
    setInputRevealed(false);
    onChange("");
  }, [onChange]);

  const beginClose = useCallback(() => {
    if (isClosingRef.current || !isExpandedRef.current) return;
    if (closeTimeoutRef.current != null) window.clearTimeout(closeTimeoutRef.current);

    inputRef.current?.blur();
    isClosingRef.current = true;
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = undefined;
      setInputRevealed(false);
    }, ENTITY_FINDER_POPOVER_CLOSE_MS);
  }, []);

  const handleInputAnimationComplete = useCallback(() => {
    if (!isExpandedRef.current) return;

    if (inputRevealed) {
      focusInput();
      return;
    }

    if (!isClosingRef.current) return;
    isClosingRef.current = false;
    collapse();
  }, [collapse, focusInput, inputRevealed]);

  // Sync with external value (e.g. empty-state clear, or restoring a query).
  useEffect(() => {
    if (value.trim().length > 0) {
      if (!isExpandedRef.current) {
        isExpandedRef.current = true;
        setIsExpanded(true);
        setInputRevealed(true);
      }
      return;
    }
    if (
      isExpandedRef.current &&
      !isClosingRef.current &&
      document.activeElement !== inputRef.current
    ) {
      beginClose();
    }
  }, [beginClose, value]);

  function clearAndClose() {
    onChange("");
    beginClose();
  }

  const markAllDisabled = !canMarkAllSeen || isMarkingAll;
  const markAllButton = (
    <button
      type="button"
      disabled={markAllDisabled}
      onClick={() => {
        void Promise.resolve(onMarkAllSeen());
      }}
      className={cn(
        SURFACE_BACK_BTN_CLS,
        "disabled:pointer-events-none disabled:opacity-50",
      )}
    >
      <CheckCheck className={SURFACE_ICON_GLYPH_CLS} aria-hidden />
      Mark all as seen
    </button>
  );

  return (
    <div className="flex items-center gap-2">
      {showMarkAll ? (
        !canMarkAllSeen && !isMarkingAll ? (
          <Tooltip>
            {/* Disabled buttons swallow hover, so the span carries the tooltip. */}
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex shrink-0 cursor-not-allowed rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {markAllButton}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">{MARK_ALL_DISABLED_REASON}</TooltipContent>
          </Tooltip>
        ) : (
          markAllButton
        )
      ) : null}
      {afterMarkAll}

      {/* Width animates (not scaleX) so neighbours slide in step with the field. */}
      <div
        className={cn(
          "relative h-9 shrink-0 transition-[width] ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
          inputRevealed ? EXPANDED_WIDTH_CLS : "w-9",
        )}
        style={{ transitionDuration: `${ENTITY_FINDER_INPUT_ANIMATION_MS}ms` }}
      >
        {!isExpanded ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={expandAndFocus}
                className={cn(SURFACE_ICON_BTN_CLS, "inline-flex items-center justify-center")}
                aria-label="Search updates"
              >
                <Search className={SURFACE_ICON_GLYPH_CLS} aria-hidden />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">Search</TooltipContent>
          </Tooltip>
        ) : null}

        <AnimatePresence initial={false}>
          {isExpanded ? (
            <motion.div
              key="updates-search-panel"
              initial={{ opacity: 0 }}
              animate={{ opacity: inputRevealed ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={PANEL_TRANSITION}
              className="absolute inset-0"
            >
              <motion.div
                initial={false}
                animate={{ opacity: inputRevealed ? 1 : 0.85 }}
                transition={INPUT_REVEAL_TRANSITION}
                className="h-full overflow-hidden rounded-full"
                onAnimationComplete={handleInputAnimationComplete}
              >
                <label data-updates-search className="relative block w-full">
                  <span className="sr-only">Filter updates</span>
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden
                  />
                  <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                    onBlur={() => {
                      if (isClosingRef.current) return;
                      if (value.trim().length === 0) beginClose();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        event.preventDefault();
                        clearAndClose();
                      }
                    }}
                    placeholder={placeholder}
                    autoComplete="off"
                    className={cn(SURFACE_SEARCH_CLS, value ? "pr-9" : "pr-3")}
                  />
                  {value ? (
                    <button
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                      }}
                      onClick={clearAndClose}
                      className="absolute right-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label="Clear search"
                    >
                      <X className="size-4" aria-hidden />
                    </button>
                  ) : null}
                </label>
              </motion.div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
